import { Component, OnDestroy, OnInit } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CiaoFormsModule } from '~app/components/shared/forms/forms.module';
import { MatSelectModule } from '@angular/material/select';
import { tap } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DatetimeService } from '~app/services/datetime.service';

type CellRendererParams = {
  regionList?: any;
};
@Component({
  selector: 'ciao-pinned-cell-renderer',
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    ReactiveFormsModule,
    CiaoFormsModule,
    MatIconModule,
  ],
  templateUrl: './pinned-cell-renderer.component.html',
  styleUrl: './pinned-cell-renderer.component.less',
})
export class PinnedCellRendererComponent implements OnInit, OnDestroy {
  params: ICellRendererParams & CellRendererParams;
  value;
  fieldType: string = 'text';
  placeholder: string;
  disabled: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(private readonly dateTimeService: DatetimeService) {}

  ngOnInit(): void {
    this.dateTimeService.timeZone$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.params) {
          this.setValue(this.params);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  agInit(params: ICellRendererParams & CellRendererParams): void {
    this.params = params;
    this.setFieldProperties(params);
    this.setValue(params);
  }

  private setFieldProperties(params) {
    this.fieldType = params?.fieldType;
    this.placeholder = params?.placeholder;
    this.disabled = params?.disabled;
  }

  private setValue(params: ICellRendererParams & CellRendererParams) {
    this.value = params.value;

    //handle waypoint county format
    if (this.params?.colDef?.field === 'county' && params?.data?.county) {
      this.value = `${params?.data?.county?.state.stateName} | ${params?.data?.county?.countyName} | ${params?.data?.county?.id}`;
    }

    //handle checkpoint date/time format
    if (
      this.params?.colDef?.field === 'expectedTime' &&
      params?.data?.expectedTime
    ) {
      let day;
      let time;
      const rawVal = params.value;
      if (!rawVal || typeof rawVal !== 'string') {
        return false;
      }
      const normalized = this.dateTimeService.normalizeToUtcIfNoOffset(rawVal);
      const date = new Date(normalized);
      const tz =
        this.dateTimeService.timeZone$.value ||
        this.dateTimeService.browserDefaults.timeZone;
      day = Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: tz,
      })
        .format(date)
        .replace(',', '');

      time = Intl.DateTimeFormat('en-US', {
        timeStyle: 'short',
        timeZone: tz,
      })
        .format(date)
        .replace(',', '');
      this.value = `${day} ${time}`;
    }

    //handle regionID format
    if (this.params?.colDef?.field === 'regionId' && params?.data?.regionId) {
      this.value =
        this.params?.regionList.find((option) => option.value === params.value)
          ?.label || params.value;
    }
  }

  refresh(params: ICellRendererParams & CellRendererParams): boolean {
    this.params = params;
    this.setValue(params);
    return true;
  }
}
